interface WishlistItem {
  name: string;
  id: number;
  slot: string;
  score_by_spec: { [spec: string]: { score: number; percentage: number } };
  wishes: Array<{
    specialization: string;
    percentage: number;
    absolute: number;
  }>;
}

interface Character {
  id: number;
  name: string;
  realm: string;
  class: string;
  role: string;
  instances: Array<{
    id: number;
    name: string;
    difficulties: Array<{
      difficulty: string;
      wishlist: {
        encounters: Array<{
          name: string;
          encounter_percentage: number;
          encounter_absolute: number;
          items: WishlistItem[];
        }>;
      };
    }>;
  }>;
}

interface WishlistResponse {
  characters: Character[];
}

class WishlistApiService {
  private proxyUrl = 'https://us-central1-just-vibes-99d61.cloudfunctions.net/wowauditWishlistProxy';
  private cache: { data: Character[]; timestamp: number } | null = null;
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  async fetchWishlistData(): Promise<Character[]> {
    // Check cache first
    if (this.cache && Date.now() - this.cache.timestamp < this.cacheTimeout) {
      return this.cache.data;
    }

    try {
      const response = await fetch(this.proxyUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: WishlistResponse = await response.json();
      const characters = data.characters || [];

      // Update cache
      this.cache = {
        data: characters,
        timestamp: Date.now()
      };

      return characters;
    } catch (error) {
      console.error('Error fetching wishlist data:', error);
      throw new Error('Failed to fetch wishlist data. Please try again.');
    }
  }

  getPlayerPriorityForBoss(playerName: string, bossName: string, characters: Character[]): number {
    const character = characters.find(char => 
      char.name.toLowerCase() === playerName.toLowerCase()
    );
    
    if (!character) return 0;

    const mannaforgeInstance = character.instances.find(inst => inst.name === 'Manaforge Omega');
    if (!mannaforgeInstance) return 0;

    const mythicDifficulty = mannaforgeInstance.difficulties.find(diff => diff.difficulty === 'mythic');
    if (!mythicDifficulty) return 0;

    // Handle boss name variations
    const normalizedBossName = bossName === 'Loomithar' ? 'Loom\'ithar' : bossName;
    
    const encounter = mythicDifficulty.wishlist.encounters.find(enc => 
      enc.name === normalizedBossName
    );
    
    if (!encounter) return 0;

    return encounter.encounter_percentage;
  }

  getPlayerWishlistItems(playerName: string, bossName: string, characters: Character[]): WishlistItem[] {
    const character = characters.find(char => 
      char.name.toLowerCase() === playerName.toLowerCase()
    );
    
    if (!character) return [];

    const mannaforgeInstance = character.instances.find(inst => inst.name === 'Manaforge Omega');
    if (!mannaforgeInstance) return [];

    const mythicDifficulty = mannaforgeInstance.difficulties.find(diff => diff.difficulty === 'mythic');
    if (!mythicDifficulty) return [];

    const normalizedBossName = bossName === 'Loomithar' ? 'Loom\'ithar' : bossName;
    
    const encounter = mythicDifficulty.wishlist.encounters.find(enc => 
      enc.name === normalizedBossName
    );
    
    if (!encounter) return [];

    return encounter.items;
  }

  getAllBosses(): string[] {
    return ['Plexus Sentinel', 'Soulbinder Naazindhri', 'Loom\'ithar'];
  }

  clearCache(): void {
    this.cache = null;
  }
}

export const wishlistApi = new WishlistApiService();
export type { Character, WishlistItem };
